import { LoadingIndicator, Alert, Button, StatusBadge, Surface } from '../ui/Primitives';
import React from 'react';
import { createPortal } from 'react-dom';


function displayedValue(field, value, voiceNames, calendarEventNames = {}) {
  if (field.key === 'languageSettings') {
    const names = new Intl.DisplayNames(['en'], { type: 'language' });
    const label = code => { try { return names.of(code); } catch (_) { return code; } };
    return `Default: ${label(value?.defaultLanguage || 'en')}`;
  }
  if (field.key === 'calendarProvider') {
    if (!value) return 'No calendar connected';
    return ({ google: 'Google Calendar', calendly: 'Calendly' })[value]
      || String(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  }
  if (field.key === 'meetingSettings') {
    if (!value?.provider) return 'Existing calendar behavior (no new meeting preference)';
    const labels = { default: 'Use Calendly event type', none: 'No location', google_conference: 'Google Meet', zoom_conference: 'Zoom', microsoft_teams_conference: 'Microsoft Teams', webex_conference: 'Webex', gotomeeting_conference: 'GoToMeeting', physical: 'In person', inbound_call: 'Caller phones us', outbound_call: 'We phone the caller', custom: 'Supplied link', ask_invitee: 'Caller supplies location' };
    const sendsInvitation = value.sendInvites || ['google_conference', 'custom'].includes(value.kind);
    const confirmation = value.provider === 'calendly'
      ? 'Notifications follow Calendly settings'
      : sendsInvitation ? 'Calendar invitations on' : 'Calendar invitations off';
    return `${value.provider === 'google' ? 'Google Calendar' : 'Calendly'} · ${labels[value.kind] || value.kind}${value.location ? ` · ${value.location}` : ''} · ${confirmation}`;
  }
  if (field.key === 'voiceId') return value ? voiceNames[value] || 'Selected voice' : 'Not set';
  if (field.key === 'availability') {
    try {
      const schedule = typeof value === 'string' ? JSON.parse(value) : value;
      const labels = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
      const days = Object.keys(labels).filter(day => schedule?.[day]?.length);
      return days.length
        ? days.map(day => `${labels[day]} ${schedule[day].join(', ')}`).join(' · ')
        : 'Not set';
    } catch (_error) {
      return 'Not set';
    }
  }
  if (field.key === 'duration' && value) return `${value} minutes`;
  if (field.key === 'calendarId') return value ? calendarEventNames[value] || `Unavailable event type · ${String(value).split('/').pop()}` : 'Not set';
  if (field.key === 'leadTime' && value !== '' && value !== null && value !== undefined) {
    return `${value} day${Number(value) === 1 ? '' : 's'}`;
  }
  if (value === false) return 'Off';
  if (value === true) return 'On';
  if (Array.isArray(value)) return value.length ? value.join('\n') : 'Not set';
  if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
  return value === 0 ? '0' : value || 'Not set';
}

function groupedChanges(fields) {
  return (fields || []).reduce((groups, field) => {
    const keyParts = String(field.key || '').split(':');
    const groupKey = keyParts[0] === 'workflow' || keyParts[0] === 'service'
      ? keyParts.slice(0, 2).join(':')
      : field.group || 'receptionist';
    const previous = groups[groups.length - 1];
    if (previous?.key === groupKey) {
      previous.fields.push(field);
      return groups;
    }
    groups.push({
      key: groupKey,
      title: field.group || 'Receptionist settings',
      category: field.category || 'Receptionist',
      fields: [field],
    });
    return groups;
  }, []);
}

const ITEM_CHANGE_LABELS = {
  added: 'Added',
  removed: 'Removed',
  updated: 'Updated',
  moved: 'Moved',
};

function emptyReviewValue(value) {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function fieldChangeType(field) {
  if (emptyReviewValue(field.published) && !emptyReviewValue(field.draft)) return 'added';
  if (!emptyReviewValue(field.published) && emptyReviewValue(field.draft)) return 'removed';
  return 'updated';
}

function ReviewChangeRow({ changeType, details, label }) {
  const normalizedType = ITEM_CHANGE_LABELS[changeType] ? changeType : 'updated';
  return (
    <div className={`receptionist-review-change-row is-${normalizedType}`}>
      <span className="receptionist-review-change-type">{ITEM_CHANGE_LABELS[normalizedType]}</span>
      <strong>{label}</strong>
      <div className="receptionist-review-change-details">
        {details.map(detail => (
          <div className="receptionist-review-change-detail" key={detail.label || 'value'}>
            {detail.label ? <small>{detail.label}</small> : null}
            <p>{detail.value}</p>
            {detail.previous !== undefined && detail.previous !== detail.value ? (
              <p className="receptionist-review-previous"><span>Was:</span> {detail.previous}</p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewItemChange({ fieldLabel, item }) {
  const changeType = ITEM_CHANGE_LABELS[item.changeType] ? item.changeType : 'updated';
  const details = (item.details || []).reduce((visible, detail) => {
    const value = changeType === 'removed' ? detail.published : detail.draft;
    const isRedundantIdentity = String(value) === String(item.label);
    const isUnneededPosition = detail.label === 'Position' && changeType !== 'moved';
    if (isRedundantIdentity || isUnneededPosition) return visible;
    visible.push({
      label: detail.label,
      value,
      previous: changeType === 'updated' || changeType === 'moved'
        ? detail.published
        : undefined,
    });
    return visible;
  }, []);
  return (
    <ReviewChangeRow
      changeType={changeType}
      details={details}
      label={`${fieldLabel} · ${item.label}`}
    />
  );
}

function reviewCounts(groups) {
  const counts = { added: 0, updated: 0, removed: 0, moved: 0 };
  groups.forEach(group => group.fields.forEach(field => {
    if (field.items?.length) {
      field.items.forEach(item => {
        const changeType = ITEM_CHANGE_LABELS[item.changeType] ? item.changeType : 'updated';
        counts[changeType] += 1;
      });
      return;
    }
    counts[fieldChangeType(field)] += 1;
  }));
  return counts;
}

function publishedAtLabel(value) {
  if (!value) return 'Not published yet';
  const publishedAt = new Date(value);
  if (Number.isNaN(publishedAt.getTime())) return 'Published';
  return `Last published ${new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(publishedAt)}`;
}


function ReceptionistPublishPanel({
  configuration,
  error,
  isAdmin,
  isDiscarding,
  isOpeningReview,
  isPublishing,
  onCloseReview,
  onDiscard,
  onOpenReview,
  onPublish,
  reviewOpen,
  success,
  voiceNames,
  calendarEventNames = {},
}) {
  if (!configuration) {
    return (
      <>
        <Surface className="receptionist-publish-panel loading">
          {error ? 'Publishing status is unavailable.' : <LoadingIndicator label="Loading publishing status…" />}
        </Surface>
        {error ? <Alert tone="danger" title="Publishing needs attention">{error}</Alert> : null}
      </>
    );
  }

  const hasChanges = configuration.hasUnpublishedChanges;
  const draftComplete = Boolean(
    configuration.draft?.greeting?.trim()
    && configuration.draft?.voiceId,
  );
  const reviewGroups = groupedChanges(configuration.changedFields);
  const counts = reviewCounts(reviewGroups);
  const changeCount = Object.values(counts).reduce((total, count) => total + count, 0);
  return (
    <>
      <Surface className={`receptionist-publish-panel ${hasChanges ? 'has-changes' : 'is-published'}`}>
        <div>
          <div className="receptionist-publish-status">
            <StatusBadge status={hasChanges ? 'warning' : 'success'} dot={false}>
              {hasChanges ? 'Unpublished changes' : 'Published'}
            </StatusBadge>
            <span>{publishedAtLabel(configuration.publishedAt)}</span>
          </div>
          <h2>{hasChanges ? 'Your draft is safe' : 'Your live Receptionist is up to date'}</h2>
          <p>{hasChanges
            ? 'Callers will continue using the live greeting, voice, services, and intake until you publish.'
            : 'Receptionist changes are saved as a draft before they affect callers.'}</p>
        </div>
        {hasChanges && isAdmin ? (
          <div className="receptionist-publish-actions">
            <Button variant="quiet" onClick={onDiscard} disabled={isPublishing || isOpeningReview} loading={isDiscarding} loadingLabel="Discarding…">
              Discard changes
            </Button>
            <Button onClick={onOpenReview} disabled={isDiscarding || isPublishing} loading={isOpeningReview} loadingLabel="Preparing review…">
              Review and publish
            </Button>
          </div>
        ) : null}
        {!isAdmin ? <small>Only an organization admin can edit and publish Receptionist changes.</small> : null}
      </Surface>

      {success ? <Alert className="receptionist-publish-success" tone="success" title="Your Receptionist is updated">The updates are ready for new callers.</Alert> : null}
      {error ? <Alert tone="danger" title="Publishing needs attention">{error}</Alert> : null}

      {reviewOpen ? createPortal(
        <div className="receptionist-publish-modal-backdrop" role="presentation" onMouseDown={onCloseReview}>
          <div
            className="receptionist-publish-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="receptionist-publish-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <span className="say-eyebrow">Ready when you are</span>
            <h2 id="receptionist-publish-title">Review Receptionist changes</h2>
            <p>These updates will be used for calls that begin after publishing.</p>
            {configuration.changedFields?.some(field => field.key === 'calendarProvider')
              ? <p>Discarding this draft won’t undo calendar connections made in Integrations.</p>
              : null}
            <div className="receptionist-publish-review-summary">
              <strong>{changeCount} change{changeCount === 1 ? '' : 's'} across {reviewGroups.length} section{reviewGroups.length === 1 ? '' : 's'}</strong>
              <div>
                {Object.entries(counts).filter(([, count]) => count > 0).map(([changeType, count]) => (
                  <span className={`is-${changeType}`} key={changeType}>{count} {ITEM_CHANGE_LABELS[changeType]}</span>
                ))}
              </div>
            </div>
            <div className="receptionist-publish-review">
              {reviewGroups.map(group => (
                <section className="receptionist-publish-review-group" key={group.key}>
                  <header>
                    <h3>{group.title} <span>· {group.category}</span></h3>
                  </header>
                  <div className="receptionist-review-change-list">
                    {group.fields.map(field => (
                      field.items?.length ? field.items.map(item => (
                        <ReviewItemChange fieldLabel={field.label} item={item} key={`${field.key}:${item.key}`} />
                      )) : (
                        <ReviewChangeRow
                          changeType={fieldChangeType(field)}
                          details={[{
                            value: displayedValue(
                              field,
                              fieldChangeType(field) === 'removed' ? field.published : field.draft,
                              voiceNames,
                              calendarEventNames,
                            ),
                            previous: fieldChangeType(field) === 'updated'
                              ? displayedValue(field, field.published, voiceNames, calendarEventNames)
                              : undefined,
                          }]}
                          key={field.key}
                          label={field.label}
                        />
                      )
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {!draftComplete ? <Alert tone="warning" title="Finish the draft">Add a greeting and choose a voice before publishing.</Alert> : null}
            <div className="receptionist-publish-modal-actions">
              <Button variant="secondary" onClick={onCloseReview} disabled={isPublishing}>Keep editing</Button>
              <Button onClick={onPublish} disabled={!draftComplete} loading={isPublishing} loadingLabel="Publishing…">
                Publish changes
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}


export default ReceptionistPublishPanel;
